import { createHash } from "node:crypto";
import type { MemoryFile } from "../dream/types.js";
import type {
  NormalizedTeachingInput,
  TeachingDelta,
  TeachingEvidenceRef,
} from "./types.js";

const SAFE_MEMORY_PATH = /^memory\/[a-z0-9][a-z0-9-]{0,119}\.md$/;
const SAFE_SEED_SLUG = /^[a-z0-9][a-z0-9-]{0,79}$/;
const SAFE_ID = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,239}$/;

export class TeachingPathError extends Error {
  constructor(readonly path: string) {
    super("Teaching proposal contains a path outside the structural allowlist");
    this.name = "TeachingPathError";
  }
}

export function teachingSummaryHash(summary: {
  version: number;
  assistantItemId: string;
  text: string;
}): string {
  return createHash("sha256").update(JSON.stringify(summary)).digest("hex");
}

function validGitBranch(branch: string): boolean {
  const hasControl = Array.from(branch).some((character) => {
    const code = character.codePointAt(0) ?? 0;
    return code < 0x20 || code === 0x7f;
  });
  return branch.length > 0 && branch.length <= 240
    && !branch.startsWith("/") && !branch.endsWith("/") && !branch.endsWith(".")
    && !branch.includes("..") && !branch.includes("@{") && !branch.includes("//")
    && !hasControl && !/[~^:?*[\\\]]/.test(branch);
}

/** Returns a safe, non-sensitive failure description, or null. */
export function validateTeachingInput(input: NormalizedTeachingInput): string | null {
  const envelope = input as { schemaVersion?: unknown; sanitized?: unknown };
  if (envelope.schemaVersion !== 1 || envelope.sanitized !== true) return "unsupported or unsanitized snapshot";
  if (input.episodeId.length > 120 || !/^teach_[A-Za-z0-9_-]+$/.test(input.episodeId)) return "invalid teaching episode id";
  if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,119}$/.test(input.worker)) return "invalid worker";
  if (!Number.isInteger(input.approvedSummary.version) || input.approvedSummary.version < 1) return "invalid approved summary version";
  if (!input.approvedSummary.text.trim() || input.approvedSummary.text.length > 12_000) return "invalid approved summary";
  if (!SAFE_ID.test(input.approvedSummary.assistantItemId)) return "invalid approved summary item id";
  if (input.approvedSummary.hash !== teachingSummaryHash({
    version: input.approvedSummary.version,
    assistantItemId: input.approvedSummary.assistantItemId,
    text: input.approvedSummary.text,
  })) return "approved summary hash mismatch";
  const confirmedAt = new Date(input.confirmedAt);
  if (!Number.isFinite(confirmedAt.getTime()) || confirmedAt.toISOString() !== input.confirmedAt) return "invalid confirmation timestamp";
  if (!/^[a-z0-9](?:[a-z0-9_.-]{0,99})\/[a-z0-9](?:[a-z0-9_.-]{0,99})$/i.test(input.target.repo)) return "invalid pinned repository";
  if (!validGitBranch(input.target.branch)) return "invalid pinned branch";
  if (!/^[a-f0-9]{64}$/.test(input.target.fingerprint)) return "invalid target fingerprint";
  const consoleUrl = (input as { evidenceConsoleUrl?: unknown }).evidenceConsoleUrl;
  if (consoleUrl !== undefined) {
    // It is rendered as a Markdown link in a public proposal, so anything that
    // could close the link early or carry a second target is refused.
    if (
      typeof consoleUrl !== "string"
      || consoleUrl.length > 2_048
      || !/^https:\/\/[^\s()<>[\]"'\\]+$/.test(consoleUrl)
    ) return "invalid evidence console url";
  }
  const teacher = (input as { teacher?: unknown }).teacher;
  if (teacher !== undefined) {
    if (!teacher || typeof teacher !== "object" || Array.isArray(teacher)) return "invalid teacher attribution";
    const { name, note } = teacher as { name?: unknown; note?: unknown };
    if (name !== undefined && (typeof name !== "string" || name.length > 80)) return "invalid teacher name";
    if (note !== undefined && (typeof note !== "string" || note.length > 500)) return "invalid teacher note";
  }
  if (input.physicalConversationIds.length === 0 || input.physicalConversationIds.length > 50) return "invalid physical conversation scope";
  if (new Set(input.physicalConversationIds).size !== input.physicalConversationIds.length) return "duplicate physical conversation id";
  if (!input.physicalConversationIds.every((id) => SAFE_ID.test(id))) return "invalid physical conversation id";
  if (input.items.length === 0 || input.items.length > 500) return "invalid evidence item count";
  if (input.items.reduce((total, item) => total + item.text.length, 0) > 500_000) return "evidence snapshot exceeds size bound";
  const ids = new Set<string>();
  let primary = 0;
  for (const item of input.items) {
    const rawItem = item as { role?: unknown; authority?: unknown };
    if (!SAFE_ID.test(item.id) || ids.has(item.id)) return "invalid or duplicate evidence item id";
    ids.add(item.id);
    if (rawItem.role !== "user" && rawItem.role !== "assistant" && rawItem.role !== "tool") return "invalid evidence item role";
    if (rawItem.authority !== "primary" && rawItem.authority !== "context") return "invalid evidence authority";
    if (!input.physicalConversationIds.includes(item.conversationId)) return "evidence item is outside physical conversation scope";
    if (!item.text || item.text.length > 40_000) return "invalid evidence item text";
    if (item.authority === "primary") primary += 1;
  }
  if (primary === 0) return "snapshot has no primary teaching evidence";
  if (!ids.has(input.approvedSummary.assistantItemId)
      || input.items.find((item) => item.id === input.approvedSummary.assistantItemId)?.authority !== "primary"
      || input.items.find((item) => item.id === input.approvedSummary.assistantItemId)?.role !== "assistant") {
    return "approved summary item is not primary evidence";
  }
  return null;
}

export function evidenceItemIds(delta: TeachingDelta): string[] {
  if (delta.verb === "no-op") return [];
  const evidence = delta.verb === "hold" ? delta.evidence : delta.evidence;
  return evidence ? [...new Set(evidence.itemIds)].sort() : [];
}

export function checkTeachingEvidence(
  evidence: TeachingEvidenceRef,
  input: NormalizedTeachingInput,
): string | null {
  if (evidence.episodeId !== input.episodeId) return "candidate cites a different teaching episode";
  const primary = new Set(input.items.filter((item) => item.authority === "primary").map((item) => item.id));
  if (evidence.itemIds.length === 0) return "candidate has no primary evidence";
  if (new Set(evidence.itemIds).size !== evidence.itemIds.length) return "candidate repeats an evidence item id";
  if (evidence.itemIds.some((id) => !primary.has(id))) return "candidate cites context-only or unknown evidence";
  return null;
}

export function allowedPathsForTeachingDelta(delta: TeachingDelta, at: Date): string[] {
  switch (delta.verb) {
    case "new":
      if (!SAFE_MEMORY_PATH.test(delta.path) || delta.path === "memory/MEMORY.md") throw new TeachingPathError(delta.path);
      return [delta.path, "memory/MEMORY.md"];
    case "supersede":
      if (!SAFE_MEMORY_PATH.test(delta.oldPath) || delta.oldPath === "memory/MEMORY.md") throw new TeachingPathError(delta.oldPath);
      if (!SAFE_MEMORY_PATH.test(delta.newPath) || delta.newPath === "memory/MEMORY.md") throw new TeachingPathError(delta.newPath);
      if (delta.oldPath === delta.newPath) throw new TeachingPathError(delta.newPath);
      return [delta.oldPath, delta.newPath, "memory/MEMORY.md"];
    case "retract":
      if (!SAFE_MEMORY_PATH.test(delta.path) || delta.path === "memory/MEMORY.md") throw new TeachingPathError(delta.path);
      return [delta.path, "memory/MEMORY.md"];
    case "skill-seed":
      if (!SAFE_SEED_SLUG.test(delta.slug)) throw new TeachingPathError(delta.slug);
      return [`inbox/${at.toISOString().slice(0, 10)}/${delta.slug}.md`];
    case "hold":
    case "no-op":
      return [];
  }
}

export function episodeAlreadyConsumed(episodeId: string, memory: MemoryFile[]): boolean {
  return memory.some((file) => {
    const source = file.frontmatter.source;
    return source === episodeId || (Array.isArray(source) && source.includes(episodeId));
  });
}

const SECRET_PATTERNS: RegExp[] = [
  /-----BEGIN (?:RSA |EC |OPENSSH )?PRIVATE KEY-----/i,
  /\bgh[opusr]_[A-Za-z0-9]{20,}\b/,
  /\bsk-(?:proj-)?[A-Za-z0-9_-]{20,}\b/,
  /\b(?:password|passwd|secret|access[_ -]?token|api[_ -]?key)\s*[:=]\s*[^\s]{6,}/i,
  /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/,
  /https?:\/\/[^\s]+\.blob\.core\.windows\.net\/[^\s]+/i,
];

const HIGH_RISK_PII_PATTERNS: RegExp[] = [
  /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i,
  /\b\d{3}-\d{2}-\d{4}\b/,
  /\b(?:\d[ -]*?){13,19}\b/,
  /\b(?:\+?\d{1,3}[ .-]?)?(?:\(?\d{2,4}\)?[ .-]?)\d{3}[ .-]?\d{4}\b/,
];

/**
 * Shared screen: anything rendered into a public proposal passes through here
 * first. Returns the category only — never the matching text.
 */
export function sensitiveMaterial(text: string): "credential" | "personal-data" | null {
  if (SECRET_PATTERNS.some((pattern) => pattern.test(text))) return "credential";
  if (HIGH_RISK_PII_PATTERNS.some((pattern) => pattern.test(text))) return "personal-data";
  return null;
}

export function checkTeachingPublicSummary(input: NormalizedTeachingInput): string | null {
  const summary = input.approvedSummary.text;
  const sensitive = sensitiveMaterial(summary);
  if (sensitive) return `approved summary contains ${sensitive}-like material`;
  const normalized = summary.replace(/\s+/g, " ").trim();
  if (input.items.some((item) => {
    const turn = item.text.replace(/\s+/g, " ").trim();
    return turn.length >= 48 && normalized.includes(turn);
  })) return "approved summary reproduces a complete evidence turn verbatim";
  return null;
}

function candidateText(delta: TeachingDelta): string {
  switch (delta.verb) {
    case "new": return `${delta.path}\n${delta.title}\n${delta.body}`;
    case "supersede": return `${delta.oldPath}\n${delta.newPath}\n${delta.title}\n${delta.body}`;
    case "retract": return `${delta.path}\n${delta.reason}`;
    case "skill-seed": return `${delta.slug}\n${delta.title}\n${delta.description}\n${delta.body}`;
    case "hold": return delta.reason;
    case "no-op": return delta.detail;
  }
}

/**
 * Backstop only; the gateway snapshot sanitizer remains the first privacy gate.
 * Rejects obvious credentials/temporary URLs and verbatim replay of a complete
 * evidence turn. It never returns the matching sensitive text.
 */
export function checkTeachingPrivacy(
  delta: TeachingDelta,
  input: NormalizedTeachingInput,
): string | null {
  const output = candidateText(delta);
  const sensitive = sensitiveMaterial(output);
  if (sensitive) return `candidate contains ${sensitive}-like or temporary-location material`;
  const normalizedOutput = output.replace(/\s+/g, " ").trim();
  for (const item of input.items) {
    const turn = item.text.replace(/\s+/g, " ").trim();
    if (turn.length >= 48 && normalizedOutput.includes(turn) && !input.approvedSummary.text.includes(turn)) {
      return "candidate reproduces a complete evidence turn verbatim";
    }
  }
  return null;
}

/** Content may teach behavior, but it may not change the proposal processor's authority. */
export function checkTeachingContentAuthority(delta: TeachingDelta): string | null {
  const text = candidateText(delta);
  const authorityChange = /\b(auto[- ]?merge|merge without review|bypass (?:human )?review|grant (?:admin|owner|permission|access)|write (?:to|outside) (?:the )?(?:repo|repository)|change (?:the )?(?:workflow|brain\.yaml|process config))\b/i;
  return authorityChange.test(text) ? "candidate attempts to widen write, permission, or review authority" : null;
}
